import type { UtilityAction } from './api';

const USAGE_ACTION_KEY = 'get_usage';

export interface TimeframeOption {
  value: string;
  label: string;
}

export interface UsageActionHomeWidgetMeta {
  timeframe: string;
  comparisonTimeframe: string;
  summaryLabel: string;
  chartDayCount: number;
  /** Handler-instructed "simple" display: forces current week + no filter controls. */
  simple: boolean;
}

export interface UsageActionDetailSummaryMeta {
  comparisonTimeframe: string;
  showForTimeframes: string[];
}

export interface UsageActionUiMeta {
  actionKey: string;
  timeframeOptions: TimeframeOption[];
  defaultTimeframe: string;
  viewModes: Array<{ value: 'delta' | 'cumulative'; label: string }>;
  defaultViewMode: 'delta' | 'cumulative';
  homeWidget: UsageActionHomeWidgetMeta;
  detailSummary: UsageActionDetailSummaryMeta;
  defaultRegisterCodes?: string[];
  primaryRegisterCode?: string;
  utilityTypeName?: string;
  /** Handler-advertised kind (water/electric/...) — drives chart kind/colour/icon. */
  utilityKind?: string;
  /** Optional explicit presentation overrides from the handler. */
  chartKind?: string;
  presentationColor?: string;
  /** Friendly, resident-facing labels per register code (e.g. ActiveEnergyPos → "Energy imported"). */
  registerLabels?: Record<string, string>;
}

interface SchemaProperty {
  type?: string;
  enum?: string[];
  default?: string | string[];
  enumLabels?: string[];
}

interface ParametersSchema {
  type?: string;
  properties?: Record<string, SchemaProperty>;
  'x-ui'?: {
    homeWidget?: Partial<UsageActionHomeWidgetMeta>;
    detail?: {
      comparisonTimeframe?: string;
      showSummaryForTimeframes?: string[];
    };
    presentation?: {
      chartKind?: string;
      color?: string;
    };
    defaultRegisterCodes?: string[];
    primaryRegisterCode?: string;
    utilityTypeName?: string;
    utilityKind?: string;
    registerFilterMode?: string;
    registerLabels?: Record<string, string>;
  };
}

function asSchemaProperty(value: unknown): SchemaProperty | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return value as SchemaProperty;
}

function parseTimeframeOptions(
  prop: SchemaProperty | undefined,
): { options: TimeframeOption[]; defaultTimeframe: string } {
  const enumValues = Array.isArray(prop?.enum)
    ? prop.enum.filter((v): v is string => typeof v === 'string')
    : [];
  const labels = Array.isArray(prop?.enumLabels)
    ? prop.enumLabels.filter((v): v is string => typeof v === 'string')
    : [];

  const options: TimeframeOption[] = enumValues.map((value, i) => ({
    value,
    label: labels[i] ?? value,
  }));

  const defaultTimeframe =
    typeof prop?.default === 'string' && enumValues.includes(prop.default)
      ? prop.default
      : (enumValues[0] ?? '7d');

  return { options, defaultTimeframe };
}

function parseViewModes(prop: SchemaProperty | undefined): {
  viewModes: UsageActionUiMeta['viewModes'];
  defaultViewMode: 'delta' | 'cumulative';
} {
  const enumValues = Array.isArray(prop?.enum)
    ? prop.enum.filter((v): v is 'delta' | 'cumulative' => v === 'delta' || v === 'cumulative')
    : [];
  const labels = Array.isArray(prop?.enumLabels)
    ? prop.enumLabels.filter((v): v is string => typeof v === 'string')
    : [];

  if (enumValues.length === 0) {
    return {
      viewModes: [
        { value: 'delta', label: 'Per-period' },
        { value: 'cumulative', label: 'Cumulative' },
      ],
      defaultViewMode: 'delta',
    };
  }

  const viewModes = enumValues.map((value, i) => ({
    value,
    label: labels[i] ?? (value === 'delta' ? 'Per-period' : 'Cumulative'),
  }));

  const defaultViewMode: 'delta' | 'cumulative' =
    typeof prop?.default === 'string' && (prop.default === 'delta' || prop.default === 'cumulative')
      ? prop.default
      : (enumValues[0] ?? 'delta');

  return { viewModes, defaultViewMode };
}

function pickTimeframe(
  value: string | undefined,
  enumValues: string[],
  fallback: string,
): string {
  if (value && enumValues.includes(value)) return value;
  return fallback;
}

function dayCountFromTimeframe(timeframe: string): number {
  const match = /^(\d+)d$/.exec(timeframe);
  if (match) return Number(match[1]);
  return 7;
}

export function findUsageChartAction(actions: UtilityAction[]): UtilityAction | undefined {
  const chartActions = actions.filter((a) => a.render_hint === 'chart');
  return chartActions.find((a) => a.action_key === USAGE_ACTION_KEY) ?? chartActions[0];
}

export function parseUsageActionMeta(action: UtilityAction): UsageActionUiMeta | null {
  const schema = action.parameters_schema as ParametersSchema | undefined;
  if (!schema?.properties) return null;

  const timeframeProp = asSchemaProperty(schema.properties.timeframe);
  const viewModeProp = asSchemaProperty(schema.properties.view_mode);
  const { options, defaultTimeframe } = parseTimeframeOptions(timeframeProp);
  const { viewModes, defaultViewMode } = parseViewModes(viewModeProp);

  if (options.length === 0) return null;

  const enumValues = options.map((o) => o.value);
  const xUi = schema['x-ui'];

  const homeTimeframe = pickTimeframe(
    xUi?.homeWidget?.timeframe,
    enumValues,
    defaultTimeframe,
  );
  const homeComparison = pickTimeframe(
    xUi?.homeWidget?.comparisonTimeframe,
    enumValues,
    enumValues.includes('14d') ? '14d' : defaultTimeframe,
  );
  const detailComparison = pickTimeframe(
    xUi?.detail?.comparisonTimeframe,
    enumValues,
    homeComparison,
  );

  const showForTimeframes = Array.isArray(xUi?.detail?.showSummaryForTimeframes)
    ? xUi.detail.showSummaryForTimeframes.filter((t) => enumValues.includes(t))
    : [homeTimeframe];

  const registerCodesProp = asSchemaProperty(schema.properties.register_codes);
  const defaultRegisterCodes = parseStringArrayDefault(registerCodesProp?.default)
    ?? (Array.isArray(xUi?.defaultRegisterCodes)
      ? xUi.defaultRegisterCodes.filter((c): c is string => typeof c === 'string' && c.length > 0)
      : undefined);

  return {
    actionKey: action.action_key,
    timeframeOptions: options,
    defaultTimeframe,
    viewModes,
    defaultViewMode,
    homeWidget: {
      timeframe: homeTimeframe,
      comparisonTimeframe: homeComparison,
      summaryLabel: xUi?.homeWidget?.summaryLabel ?? 'This week',
      chartDayCount: xUi?.homeWidget?.chartDayCount ?? dayCountFromTimeframe(homeTimeframe),
      // Default the home widget to the simple/current-week display unless the
      // handler explicitly opts out.
      simple: (xUi?.homeWidget as { simple?: boolean } | undefined)?.simple !== false,
    },
    detailSummary: {
      comparisonTimeframe: detailComparison,
      showForTimeframes,
    },
    defaultRegisterCodes,
    primaryRegisterCode:
      typeof xUi?.primaryRegisterCode === 'string' ? xUi.primaryRegisterCode : undefined,
    utilityTypeName:
      typeof xUi?.utilityTypeName === 'string' ? xUi.utilityTypeName : undefined,
    utilityKind: typeof xUi?.utilityKind === 'string' ? xUi.utilityKind : undefined,
    chartKind: typeof xUi?.presentation?.chartKind === 'string' ? xUi.presentation.chartKind : undefined,
    presentationColor: typeof xUi?.presentation?.color === 'string' ? xUi.presentation.color : undefined,
    registerLabels:
      xUi?.registerLabels && typeof xUi.registerLabels === 'object'
        ? Object.fromEntries(
            Object.entries(xUi.registerLabels).filter(
              ([k, v]) => typeof k === 'string' && typeof v === 'string',
            ),
          )
        : undefined,
  };
}

function parseStringArrayDefault(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const codes = value.filter((c): c is string => typeof c === 'string' && c.length > 0);
  return codes.length > 0 ? codes : undefined;
}
